import { html, TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { watch } from "../internal/watch";

import { BaseElement } from "../BaseElement";
import { LightTheme } from "../ThemeLight";

import { styles } from "./UserProfileBanner.style";
import { IUserInfo } from "../../backend/cloud/UserTable";
import { ifDefined } from "lit/directives/if-defined.js";

import "../SocialLinks/SocialLinks";
import "../ImageMediaImport/ImageMediaImport";
import { litIf } from "../LitUtils";

@customElement("ds-user-profile-banner")
export class UserProfileBanner extends BaseElement {
    static override styles = [styles, LightTheme];

    @property({ type: Object }) userInfo?: IUserInfo;
    @property({ type: String }) currentView = "Posts";

    @state() protected _avatarUrl?: string;
    @state() protected _bannerUrl?: string;

    @watch("userInfo")
    async onUserInfoUpdate() {
        if (this.userInfo) {
            const us = this._kernel.userSettings;

            this._avatarUrl = await us.getAvatarURL(this.userInfo);
            this._bannerUrl = await us.getBannerURL(this.userInfo);
        }
    }

    private _createViewButton(view: string): TemplateResult {
        return html`<span
            class="view-button ${this.currentView === view ? "active" : ""}"
            @click=${() => {
                this._raiseEvent("ds-show-view", { view });
            }}
            >${view}</span
        >`;
    }

    override render() {
        return html` <div class="container">
            <div class="banner">
                <img src=${ifDefined(this._bannerUrl)} />
                <ds-image-media-import
                    class="edit-banner-photo"
                    iconName="camera"
                    buttonText="Edit cover photo"
                    @ds-accept=${async (e: CustomEvent) => {
                        if (this.userInfo) {
                            this.userInfo.banner = e.detail.media;
                            await this._kernel.userSettings.update(this.userInfo);
                            this._bannerUrl = await this._kernel.userSettings.getBannerURL(
                                this.userInfo
                            );
                        }
                    }}
                ></ds-image-media-import>
                <ds-close-button></ds-close-button>
            </div>

            <div class="banner-footer flex-column">
                <div class="flex-row">
                    <sl-avatar image=${ifDefined(this._avatarUrl)}></sl-avatar>
                    <div class="flex-column">
                        <h1>${this.userInfo?.displayName}</h1>
                        <ds-social-links
                            .socials=${this.userInfo?.socials ?? undefined}
                        ></ds-social-links>
                    </div>
                </div>

                <hr />

                <div class="flex-row">
                    ${this._createViewButton("Posts")} ${this._createViewButton("Gallery")}
                    ${litIf(
                        this._kernel.user && this.userInfo?.owner === this._kernel.user?.id,
                        () =>
                            html`${this._createViewButton("Lists")}${this._createViewButton(
                                "Settings"
                            )}`
                    )}
                </div>
            </div>
        </div>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-user-profile-banner": UserProfileBanner;
    }
}
